import _ from 'lodash';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { serializeSectionsForSave } from './TestInstanceTemplateSectionHelper';

import {
    TEST_TYPE_CHART_Y_AXIS_FIELD,
    TEST_TYPE_CHART_STACKING_FIELD,
    CHART_STACKING_DEFAULT_KEY,
    RESERVED_METRICS,
    TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD,
} from './TestTypesDetailPageConstants';

export const METRIC_COLOR_SCALE = [
    '#7cb5ec',
    '#90ed7d',
    '#f7a35c',
    '#8085e9',
    '#f15c80',
    '#2b908f',
    '#f45b5b',
    '#91e8e1',
    '#434348',
    '#5b7f95',
    '#994878',
    '#d13239',
    '#d1d1aa',
    '#53565a',
    '#789d4a',
    '#97d700',
];

// Mirrors portal/translations/en.js: config.templates.fields.exampleScript
const EXAMPLE_SCRIPT_TEMPLATE = `import json
import sys
from ngp.v1.measurements.ping import ping
from ngp.v1.helpers.utilities import current_platform, platform_to_string


def run(test_config):
    """
    Every scripted test needs to have a function called 'run' with
    a single argument. This argument provides the script with the optionally defined
    test config variables that are defined in the test type editor and then filled in
    on the test editor.

    :param str test_config: Serialized \`\`test_config\`\` dictionary as a JSON formatted stream
    :return: Serialized \`\`results\`\` dictionary as a JSON formatted stream
    :rtype: str
    :raises Exception: example of raising an exception.
    """

    # Convert the test_config JSON string into a python dictionary for easy use
    config = json.loads(test_config)

    if config.get("target") is None:
        # If you encounter an error in your script, raise an exception and pass it a detailed message.
        raise Exception("Required target is missing from test configuration.")

    # Example calling the ping utility method
    ping_results = ping(config["target"], timeout=5)

    # To return results to nGeniusPULSE to be charted by the UI, create a Python dictionary
    #  with the keys matching the metric names that are being charted.
    #  Note:  Each key (i.e. metric name) may only use letters, numbers and underscores
    #         If the metric/output parameters are in an object named 'results'
    #         then the system will automatically create your metrics when the script is uploaded.
    results = {}
    results["latency"] = ping_results["latency"]
    results["platform"] = platform_to_string(current_platform())
    results["message"] = "Success"
    # Convert the results dictionary into a JSON string and return it.
    return json.dumps(results)


def debug():
    default_config = {"target": "127.0.0.1"}
    has_cli_config = len(sys.argv) > 1
    test_config = sys.argv[1] if (has_cli_config) else json.dumps(default_config)
    print("test_config: {0}".format(test_config))
    print("results: {0}".format(run(test_config)))


# Allows you to run this script from the command line supplying an argument
# Usage:  python example.py "{\\"target\\":127.0.0.1}"
#if __name__ == "__main__":
#    debug()
`;

export function buildExampleScript(name = 'example') {
    return EXAMPLE_SCRIPT_TEMPLATE.replace(/\{\{\s*name\s*\}\}/g, name);
}

export function makeMetricNamePretty(metricName) {
    if (!metricName) return '';
    return metricName
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

function cleanScriptParam(paramRegEx, match) {
    if (!match) {
        return '';
    }

    let cleaned = match.replace(new RegExp(`${paramRegEx}\\.`, 'i'), '');
    cleaned = cleaned.replace(new RegExp(`${paramRegEx}\\s*\\[`, 'i'), '');
    cleaned = cleaned.replace(/]/g, '');
    cleaned = cleaned.replace(/'/g, '');
    cleaned = cleaned.replace(/"/g, '');
    return _.trim(cleaned);
}

function getScriptParams(searchString, regExPattern, paramRegEx) {
    if (!searchString || !regExPattern) {
        return [];
    }

    const matches = searchString.match(regExPattern);
    if (!matches || matches.length === 0) {
        return [];
    }

    return _.uniqBy(
        _.map(matches, (match) => cleanScriptParam(paramRegEx, match)),
        _.toLower
    );
}

export function getScriptOutputMetrics(scriptText) {
    return _(getScriptParams(scriptText, / +results\[\S*]| +results\.[a-zA-Z_]*/gim, 'results'))
        .filter((param) => !_.includes(RESERVED_METRICS, _.toLower(param || '')))
        .map((param) => ({
            name: _.trim(param),
            title: makeMetricNamePretty(param),
            hidden: false,
            isMain: false,
        }))
        .value();
}

export function getNextMetricId(metrics = []) {
    const existingIds = _.chain(metrics)
        .map((metric) => _.toNumber(metric?.id))
        .value();
    return 1 + Math.max(...existingIds, 0);
}

export function getNextChartColor(metrics = [], editingInternalId = null) {
    const usedColors = new Set(
        _.chain(metrics)
            .filter((metric) => metric?.[INTERNAL_ID_FIELD] !== editingInternalId)
            .map((metric) => `${metric?.color || ''}`.trim())
            .filter((color) => !_.isEmpty(color))
            .value()
    );
    const firstAvailable = _.find(METRIC_COLOR_SCALE, (color) => !usedColors.has(color));
    return firstAvailable || METRIC_COLOR_SCALE[usedColors.size % METRIC_COLOR_SCALE.length];
}

export function mergeMetricsFromScript(existingMetrics = [], scriptText, stepMetrics = []) {
    const scriptMetrics = getScriptOutputMetrics(scriptText);

    _.forEach(stepMetrics, (metricFromStep) => {
        const matchingMetric = _.find(scriptMetrics, ['name', metricFromStep.name]);
        if (matchingMetric) {
            _.set(matchingMetric, 'beaconStep', metricFromStep.beaconStep);
            return;
        }

        scriptMetrics.push({
            ...metricFromStep,
            title: makeMetricNamePretty(metricFromStep.name),
            hidden: false,
            isMain: false,
        });
    });

    const nextMetrics = [...existingMetrics];
    _.forEach(_.differenceBy(scriptMetrics, nextMetrics, 'name'), (newMetric) => {
        const metricToAdd = {
            ...newMetric,
            id: getNextMetricId(nextMetrics),
            multiplier: 0,
            color: getNextChartColor(nextMetrics),
        };

        if (_.filter(nextMetrics, { hidden: false, isMain: true }).length === 0) {
            metricToAdd.isMain = true;
        }

        nextMetrics.push(metricToAdd);
    });

    return nextMetrics;
}

export async function normalizeDownloadBlob(responseData) {
    if (responseData instanceof Blob) {
        return responseData;
    }

    if (typeof ReadableStream !== 'undefined' && responseData instanceof ReadableStream) {
        return new Response(responseData).blob();
    }

    return new Blob([responseData], { type: 'application/zip' });
}

/**
 * Check if a script is a BTT script.
 * Mirrors portal/ui isBttScript.
 */
export function isBttScript(script) {
    if (!script) return false;
    return /from \s*ngp\.v1\.btt\.engine\s* import /.test(script);
}

/**
 * Reset metrics to left-axis if there's only 1 axis.
 * Also ensure proper zIndexing so chart looks nice.
 * Also disable the line marker on non-scatter series.
 * Mirrors portal/ui cleanUpAxesAndMetrics.
 */
export function cleanUpAxesAndMetrics(payload) {
    const isSingleAxis = _.get(payload, 'chartConfig.yAxis', []).length <= 1;
    const chartType = _.get(payload, 'chartConfig.chart.type', 'line');
    const zIndexByType = { area: 0, line: 1, scatter: 2 };

    _.forEach(payload.metrics, (metric) => {
        const metricChartType = _.get(metric, 'chartSeriesOverrides.type', null) || chartType;
        const isScatter = metricChartType === 'scatter';

        if (isSingleAxis) {
            _.unset(metric, 'chartSeriesOverrides.yAxis');
        } else if (_.get(metric, 'chartSeriesOverrides.yAxis', null) === null) {
            _.set(metric, 'chartSeriesOverrides.yAxis', 0);
        }

        _.set(
            metric,
            'chartSeriesOverrides.marker',
            isScatter ? { enabled: true, symbol: 'circle' } : { enabled: false }
        );
        _.set(metric, 'chartSeriesOverrides.zIndex', zIndexByType[metricChartType]);
    });

    return payload;
}

export function toLowerCamelCase(title) {
    return `${title || ''}`.replace(/[^A-Za-z0-9]/g, ' ').replace(/^\w|[A-Z]|\b\w|\s+/g, (match, index) => {
        if (+match === 0 || match === '-' || match === '.') {
            return '';
        }

        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

export function buildTestTypePayload(formData, chart2axis = false) {
    const payloadMetrics = _.map(formData?.metrics || [], (metric) => _.omit(metric, [INTERNAL_ID_FIELD]));
    const payloadSections = serializeSectionsForSave(_.get(formData, TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD, []));

    const rawYAxis = _.get(formData, TEST_TYPE_CHART_Y_AXIS_FIELD);
    let normalizedYAxis = [];
    if (_.isArray(rawYAxis)) {
        normalizedYAxis = [...rawYAxis];
    } else if (_.isObject(rawYAxis)) {
        normalizedYAxis = _(rawYAxis)
            .toPairs()
            .sortBy(([index]) => _.toNumber(index))
            .map(([, axis]) => axis)
            .value();
    }
    const leftAxis = normalizedYAxis[0] || { title: { text: '' } };
    const rightAxis = normalizedYAxis[1] || { opposite: true, title: { text: '' } };
    const payloadYAxis = chart2axis ? [leftAxis, rightAxis] : [leftAxis];
    const uiStackingValue = _.get(formData, TEST_TYPE_CHART_STACKING_FIELD);
    const payloadStacking = uiStackingValue === CHART_STACKING_DEFAULT_KEY ? '' : uiStackingValue;

    return {
        ...formData,
        metrics: payloadMetrics,
        testBuilder: {
            ...formData?.testBuilder,
            details: {
                ...formData?.testBuilder?.details,
                sections: payloadSections,
            },
        },
        chartConfig: {
            ...formData?.chartConfig,
            yAxis: payloadYAxis,
            plotOptions: {
                ...formData?.chartConfig?.plotOptions,
                series: {
                    ...formData?.chartConfig?.plotOptions?.series,
                    stacking: payloadStacking,
                },
            },
        },
        name: formData?.name,
        group: formData?.group,
    };
}
